/**
 * Cedolini · Riepilogo Annuale
 *
 * Matrice 12 mesi × N lavoratori con totale annuo per riga e totale mensile
 * per colonna. Export XLSX/PDF integrato.
 */
import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import api, { API_BASE } from "@/api";
import { Wallet, FileSpreadsheet, FileText, Users, ChevronLeft, ChevronRight } from "lucide-react";

const eur = (n) =>
  n
    ? new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(n)
    : "—";
const eur2 = (n) =>
  new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(n || 0);

const MONTH_LABELS = ["Gen", "Feb", "Mar", "Apr", "Mag", "Giu", "Lug", "Ago", "Set", "Ott", "Nov", "Dic"];

function downloadUrl(path) {
  // apre in nuova tab così il browser gestisce l'attachment
  const w = window.open(`${API_BASE}${path}`, "_blank", "noopener,noreferrer");
  if (w) w.opener = null;
}

export default function CedoliniRiepilogo() {
  const currentYear = new Date().getFullYear();
  const [anno, setAnno] = useState(currentYear);

  const { data, isLoading } = useQuery({
    queryKey: ["cedolini-riepilogo", anno],
    queryFn: () => api.get(`/cedolini/riepilogo-completo/${anno}`).then((r) => r.data),
    keepPreviousData: true,
  });

  const rows = data?.lavoratori || [];
  const totaliMese = data?.totali_mese || Array(12).fill(0);
  const grandTotal = data?.grand_total || 0;

  return (
    <div className="space-y-6" data-testid="page-cedolini-riepilogo">
      <div>
        <div className="overline">Contabilità · Personale</div>
        <h1 className="font-display text-3xl xl:text-4xl font-black flex items-center gap-2">
          <Wallet className="w-8 h-8 text-slate-500" /> Riepilogo Cedolini · Annuale
        </h1>
        <p className="text-sm text-slate-600 mt-1">
          Matrice 12 mesi × lavoratore. Ogni cella è la somma dei cedolini di quel mese.
          Le celle vuote indicano che il cedolino non è ancora stato compilato.
        </p>
      </div>

      {/* Anno + Export */}
      <div className="flex flex-wrap items-center gap-3 bg-white border border-slate-200 p-3">
        <div className="flex items-center gap-2">
          <button
            onClick={() => setAnno((a) => a - 1)}
            className="p-1.5 border border-slate-300 rounded-sm hover:bg-slate-50"
            data-testid="btn-anno-prev"
            aria-label="Anno precedente"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <div className="font-display font-black text-3xl px-3" data-testid="cedolini-anno-corrente">
            {anno}
          </div>
          <button
            onClick={() => setAnno((a) => a + 1)}
            className="p-1.5 border border-slate-300 rounded-sm hover:bg-slate-50"
            data-testid="btn-anno-next"
            aria-label="Anno successivo"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
          {anno !== currentYear && (
            <button
              onClick={() => setAnno(currentYear)}
              className="ml-2 text-xs text-slate-500 hover:text-slate-800 underline"
            >
              torna a {currentYear}
            </button>
          )}
        </div>

        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={() => downloadUrl(`/contabilita/export/cedolini/xlsx?anno=${anno}`)}
            data-testid="btn-cedolini-export-xlsx"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-emerald-300 text-emerald-800 hover:bg-emerald-50 rounded-sm"
          >
            <FileSpreadsheet className="w-3.5 h-3.5" /> Excel
          </button>
          <button
            onClick={() => downloadUrl(`/contabilita/export/cedolini/pdf?anno=${anno}`)}
            data-testid="btn-cedolini-export-pdf"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold border border-rose-300 text-rose-800 hover:bg-rose-50 rounded-sm"
          >
            <FileText className="w-3.5 h-3.5" /> PDF
          </button>
        </div>
      </div>

      {/* KPI */}
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <Kpi label="Lavoratori con cedolini" value={rows.length} icon={Users} testId="kpi-count-lav" />
        <Kpi label="Totale erogato" value={eur2(grandTotal)} tone="orange" testId="kpi-totale-anno" />
        <Kpi
          label="Media mensile aziendale"
          value={eur2(grandTotal / 12)}
          tone="slate"
          sub={`su ${rows.length || 0} lavoratori`}
        />
      </div>

      {/* Tabella */}
      <div className="bg-white border border-slate-200 overflow-x-auto">
        <table className="w-full text-xs" data-testid="table-cedolini-riepilogo">
          <thead className="bg-slate-900 text-white text-[10px] uppercase tracking-wider">
            <tr>
              <th className="text-left p-2 sticky left-0 bg-slate-900 z-10 min-w-[180px]">Lavoratore</th>
              {MONTH_LABELS.map((m, i) => (
                <th key={m} className="text-right p-2" data-testid={`th-mese-${i + 1}`}>{m}</th>
              ))}
              <th className="text-right p-2 bg-orange-600">Totale</th>
            </tr>
          </thead>
          <tbody className="font-mono">
            {isLoading && (
              <tr>
                <td colSpan={14} className="p-8 text-center text-slate-400 italic">Caricamento…</td>
              </tr>
            )}
            {!isLoading && rows.length === 0 && (
              <tr>
                <td colSpan={14} className="p-8 text-center text-slate-400 italic">
                  Nessun cedolino registrato per l'anno {anno}. Aggiungili dalla scheda del lavoratore.
                </td>
              </tr>
            )}
            {rows.map((r) => (
              <tr
                key={r.lavoratore_id}
                className={`border-t border-slate-100 hover:bg-slate-50 ${!r.active ? "opacity-60" : ""}`}
                data-testid={`row-lav-${r.lavoratore_id}`}
              >
                <td className="p-2 sticky left-0 bg-white z-10">
                  <Link to={`/lavoratori`} className="font-semibold hover:text-orange-700">
                    {r.full_name}
                  </Link>
                  {r.mansione && <div className="text-[10px] text-slate-500 font-sans">{r.mansione}</div>}
                  {!r.active && (
                    <span className="text-[9px] font-bold uppercase tracking-widest text-slate-500 font-sans">
                      cessato
                    </span>
                  )}
                </td>
                {r.mesi.map((v, i) => (
                  <td
                    key={i}
                    className={`p-2 text-right ${v ? "text-slate-800" : "text-slate-300"}`}
                    data-testid={`cell-${r.lavoratore_id}-${i + 1}`}
                  >
                    {v ? eur(v) : "—"}
                  </td>
                ))}
                <td className="p-2 text-right font-bold text-orange-700 bg-orange-50/40" data-testid={`total-${r.lavoratore_id}`}>
                  {eur(r.totale_anno)}
                </td>
              </tr>
            ))}
          </tbody>
          {rows.length > 0 && (
            <tfoot>
              <tr className="border-t-2 border-slate-900 bg-slate-100 font-mono">
                <td className="p-2 font-bold uppercase text-[10px] sticky left-0 bg-slate-100">
                  Totale mese
                </td>
                {totaliMese.map((v, i) => (
                  <td key={i} className="p-2 text-right font-bold" data-testid={`total-mese-${i + 1}`}>
                    {eur(v)}
                  </td>
                ))}
                <td className="p-2 text-right font-black text-lg text-orange-700 bg-orange-100" data-testid="grand-total">
                  {eur(grandTotal)}
                </td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>

      <div className="text-[11px] text-slate-500 italic">
        I valori sono la somma degli <b>importi_cedolino</b> effettivamente registrati mese per mese
        (paga base + straordinari). I mesi non compilati vengono considerati come 0 nei totali.
      </div>
    </div>
  );
}

function Kpi({ label, value, tone = "slate", icon: Icon, testId, sub }) {
  const tones = {
    slate: "bg-white border-slate-200 text-slate-800",
    orange: "bg-orange-50 border-orange-300 text-orange-800",
  };
  return (
    <div className={`p-4 border shadow-brutal ${tones[tone] || tones.slate}`} data-testid={testId}>
      <div className="flex items-center justify-between">
        <div className="text-[10px] uppercase tracking-widest font-bold opacity-70">{label}</div>
        {Icon && <Icon className="w-4 h-4 opacity-60" />}
      </div>
      <div className="font-display font-black text-2xl mt-1">{value}</div>
      {sub && <div className="text-[10px] opacity-70 mt-0.5">{sub}</div>}
    </div>
  );
}
